<template>
  <div class="d-flex">
    <div class="flex-grow-1 items-area mr-4 pt-4">
      <div class="d-flex">
        <div style="width: 450px;">
          <div class="qtm-h4 text-secondary text-truncate" v-text="matchItemsHeader" />
          <div class="text-mid-dark-grey mb-4" style="width: 424px">
            Drag the
            <span class="text-lowercase" v-text="itemType" />
            items from the right-hand side table and drop them below to match with {{ matchItemType }} items.
          </div>
        </div>
        <div>
          <auto-match-items
            :items="itemsToMatch"
            :loading="loading"
            :order-items="matchItems"
            @update:count="autoMatchedcount = $event"
            @update:items="onAutoMatch"
            @update:loading="$emit('update:loading', $event)"
          />
        </div>
      </div>
      <div class="bg-background px-4 py-2">
        <div class="mb-2 text-right">
           {{ matchedCount }} / {{ itemsWithMatch.length }} items matched
        </div>
        <qtm-table class="match-table" dense :headers="headers" no-items-text="">
          <template v-for="(item, index) in itemsWithMatch" :key="item.id">
            <tr>
              <td class="description-cell" v-text="item.description" />
              <td class="quantity-cell" v-text="Number(item.quantity)" />
              <td class="unit-cell" v-text="item.unit" />
              <td class="table-spacer" />
              <td
                v-if="!dragging && matchedCount === 0"
                :class="{ 'd-none': index > 0 }"
                :colspan="matchHeaders.length"
                :rowspan="itemsWithMatch.length"
              >
                <div class="empty-state text-mid-light-grey">
                  Drag and drop items to match with {{ matchItemType }} items.
                </div>
              </td>
              <td v-else class="pa-0 nested-table-cell" :colspan="matchHeaders.length">
                <draggable
                  v-bind="draggableCommon"
                  class="nested-table"
                  :data-index="index"
                  :disabled="loading"
                  :empty-insert-threshold="0"
                  ghost-class="ghost-to"
                  :model-value="[item]"
                  tag="table"
                  @change="onChange('match-items', $event, index)"
                >
                  <template v-slot:item>
                    <tr v-if="item.match && target !== index" :data-index="index">
                      <td class="match-cell">
                        <div class="d-flex align-center">
                          <div class="handle">
                            <v-icon icon="mdi-dots-vertical" />
                            <v-icon class="handle-right" icon="mdi-dots-vertical" />
                          </div>
                          <div>
                            <div>{{ item.match.description }}</div>
                            <item-exception-flag class="mt-1" :exception="item.match.exception" />
                          </div>
                        </div>
                      </td>
                      <td class="match-cell quantity-cell" v-text="item.match.quantity" />
                       <td class="match-cell unit-cell">
                         <div class="d-flex justify-space-between">
                           {{ item.match.unit }}
                           <qtm-icon-btn
                             class="edit-match-btn mt-n1"
                             color="mid-light-grey"
                             icon="mdi-pencil"
                             size="small"
                             @click="itemToEdit = item.match"
                           />
                         </div>
                      </td>
                      <td class="match-cell price-cell">
                        <span v-if="item.match" v-text="currency(item.match.unit_price)" />
                      </td>
                    </tr>
                  </template>
                </draggable>
              </td>
            </tr>
          </template>
        </qtm-table>
      </div>
    </div>
    <div class="items-area pt-4 pr-4 mr-n4" style="width: 500px">
      <div class="qtm-h4 text-secondary text-truncate" v-text="itemsToMatchHeader" />
      <div class="text-mid-dark-grey mb-4">
        Items that are left unmatched will be saved to {{ matchItemType }} as “additional”.
      </div>
      <div v-if="remainingItems.length === 0" class="empty-drag-area">
        <draggable
          v-if="remainingItems.length === 0"
          v-bind="draggableCommon"
          class="empty-draggable"
          :disabled="loading"
          :empty-insert-threshold="500"
          ghost-class="ghost-empty"
          :model-value="[]"
          tag="div"
          @change="onChange('items', $event)"
        >
          <template v-slot:item>
            <div />
          </template>
        </draggable>
        <div class="empty-state text-mid-light-grey qtm-h4">
          Drag and drop here if you want to unmatch
        </div>
      </div>
      <qtm-table
        v-else
        class="items-to-match-table"
        dense
        :headers="matchHeaders"
        no-header
        no-items-text=""
      >
        <draggable
          v-bind="draggableCommon"
          :disabled="loading"
          ghost-class="ghost-from"
          :model-value="remainingItems"
          tag="tbody"
          @change="onChange('items', $event)"
        >
          <template v-slot:item="{ element: item }">
            <tr class="item-to-match">
              <td class="d-flex align-center">
                <div class="handle">
                  <v-icon icon="mdi-dots-vertical" />
                  <v-icon class="handle-right" icon="mdi-dots-vertical" />
                </div>
                {{ item.description }}
              </td>
              <td v-text="item.quantity" />
              <td class="unit-cell--small" v-text="item.unit" />
              <td class="price-cell" v-text="currency(item.unit_price)" />
            </tr>
          </template>
        </draggable>
      </qtm-table>
    </div>
    <teleport v-if="actionTarget" :to="actionTarget">
      <qtm-btn :loading="loading" size="x-large" @click="save">
        Save
      </qtm-btn>
    </teleport>
    <edit-quantity-unit-dialog
      v-if="itemToEdit"
      :item="itemToEdit"
      :item-type="itemType"
      model-value
      :units="units"
      @update:item="editItem"
      @update:model-value="itemToEdit = null"
    />
  </div>
</template>

<script setup lang="ts">
import draggable from 'vuedraggable'
import type { LineItem } from '@quotetome/materials-api'
import AutoMatchItems from '@/components/items/auto-match-items.vue'
import EditQuantityUnitDialog from '@/components/items/edit-quantity-unit-dialog.vue'
import ItemExceptionFlag from '@/components/items/item-exception-flag.vue'
import QtmTable, { type Header } from '@/components/qtm-table.vue'
import { currency } from '@/models/filters'

export interface Props {
  actionTarget?: HTMLElement
  itemType: string
  itemsToMatch: LineItem[]
  itemsToMatchHeader: string
  loading?: boolean
  matchItems: LineItem[]
  matchItemsHeader: string
  matchItemType: string
  orderId?: number | null
  units?: any[]
}

const props = defineProps<Props>()
const emit = defineEmits(['update:items-to-match', 'update:loading'])

const matchHeaders = computed<Header[]>(() => [
  { title: `Matched ${props.itemType} Items`, value: 'match.description' },
  { title: 'QTY', value: 'match.quantity', cellClass: 'quantity-cell' },
  { title: 'U/M', value: 'match.unit', cellClass: 'unit-cell' },
  { title: 'Unit Price', value: 'match.unit_price', cellClass: 'price-cell' },
])

const headers = computed<Header[]>(() => [
  { title: `${props.matchItemType} Items`, value: 'description', cellClass: 'description-cell' },
  { title: 'QTY', value: 'quantity', cellClass: 'quantity-cell' },
  { title: 'U/M', value: 'unit', cellClass: 'unit-cell' },
  { title: '', value: '_spacer', cellClass: 'table-spacer' },
].concat(matchHeaders.value))

type LineItemWithException = LineItem & { exception?: string }

interface ItemWithMatch extends LineItem {
  match: LineItemWithException | null
}

const autoMatchedcount = ref(0)
const itemToEdit = ref<LineItem | null>(null)
const itemsWithMatch = ref<ItemWithMatch[]>([])
const remainingItems = ref<LineItem[]>([])

const matchedCount = computed(() => itemsWithMatch.value.filter(item => item.match).length)

onMounted(() => {
  itemsWithMatch.value = props.matchItems.map(item => ({
    ...item,
    match: props.itemsToMatch.find(match => match.reference_identifier === item.internal_identifier) ?? null,
  }))
  remainingItems.value = props.itemsToMatch.filter(item => !item.reference_identifier)
})

const onChange = (table: string, event: any, index?: number) => {
  if (event.added) {
    const match = event.added.element.match || event.added.element

    if (table === 'match-items') {
      if (index! < itemsWithMatch.value.length) {
        const matchItem = itemsWithMatch.value[index!]!

        if (matchItem.match) {
          remainingItems.value.push(matchItem.match)
        }

        itemsWithMatch.value.forEach(item => {
          if (item.match?.id === match.id) {
            item.match = null
          }
        })

        matchItem.match = { ...match, exception: '' }
        remainingItems.value = remainingItems.value.filter(item => item.id !== match.id)
      }
    }
    else if (table === 'items') {
      remainingItems.value.splice(event.added.newIndex, 0, match)

      itemsWithMatch.value.forEach(item => {
        if (item.match?.id === match.id) {
          item.match = null
        }
      })
    }
  }
}

const dragging = ref(false)
const target = ref<number | null>(null)

const onEnd = () => {
  dragging.value = false
  target.value = null
}

const onMove = (event: any) => {
  const value = event.related?.attributes?.['data-index']?.value

  if (value) {
    target.value = Number(value)
  }
}

const onStart = () => {
  dragging.value = true
}

const draggableCommon = {
  chosenClass: 'chosen',
  dragClass: 'dragging',
  forceFallback: true,
  group: 'items',
  handle: '.handle',
  itemKey: 'id',
  onEnd,
  onMove,
  onStart,
  sort: false,
}

const { $tracker } = useNuxtApp()
const save = () => {
  const matchedItems = itemsWithMatch.value.filter(item => item.match).map(item => ({
    ...item.match,
    reference_identifier: item.internal_identifier,
  }))
  const updatedItems = props.itemsToMatch.map(item => {
    const matched = matchedItems.find(match => match.id === item.id)

    if (matched) {
      return matched
    }

    return { ...item, reference_identifier: '' }
  })

  $tracker.track('Items Matched', {
    'automatic_matched_items': autoMatchedcount.value,
    'manually_matched_items': matchedItems.length - autoMatchedcount.value,
    'rfq_id': props.orderId ?? null,
    'total_items': props.matchItems.length,
    'total_matched_items': matchedItems.length,
    'unmatched_items': props.matchItems.length - matchedItems.length,
  })

  emit('update:items-to-match', updatedItems)
}

const onAutoMatch = async (items: LineItem[]) => {
  // await onMounted to initialize values
  await nextTick()
  items.filter(item => item.reference_identifier).forEach(item => {
    const matchItem = itemsWithMatch.value.find(match => {
      return !match.match && match.internal_identifier === item.reference_identifier
    })

    if (matchItem) {
      matchItem.match = item
      remainingItems.value = remainingItems.value.filter(i => i.id !== item.id)
    }
  })
}

const editItem = (match: LineItem) => {
  const item = itemsWithMatch.value.find(i => i.match?.id === match.id)

  if (item) {
    item.match = match
  }
}
</script>

<style lang="scss">
$color: rgb(var(--v-theme-background));

.dragging {
  display: flex;
  border: 1px solid rgb(var(--v-theme-light-grey));
  opacity: 1 !important;

  & td:first-child {
    flex-grow: 1;
  }

  & .handle {
    color: rgb(var(--v-theme-interactive));
  }
}

.handle {
  color: rgb(var(--v-theme-mid-grey));
  cursor: grab;
  display: flex;
  align-items: center;
  font-size: 0.8rem;

  &-right {
    margin-left: -14px;
  }

  &:hover {
    color: rgb(var(--v-theme-interactive));
  }
}

.table-spacer {
  background: $color !important;
  border-color: $color !important;
  width: 16px;
  max-width: 16px;
  min-width: 16px;
}

.ghost-from, .ghost-empty {
  & td {
    background: $color;
    border-color: $color;
    color: $color;
  }

  & .handle {
    display: none;
  }
}

.nested-table-cell {
  height: 1px; // Force Chrome to calculate height properly
}

.nested-table {
  height: 100%;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;

  & td {
    border-top: none;
    border-bottom: none;
    height: 100%;

    &:first-child {
      border-left: none;
    }

    &:last-child {
      border-right: none;
    }
  }
}

.ghost-to {
  $color: rgb(var(--v-theme-interactive-lighten-1));

  & td {
    background: $color;
    border-color: $color;
    color: $color;
  }

  & .handle,
  & .item-exception-flag,
  & .edit-match-btn {
    display: none;
  }
}

.description-cell {
  width: 240px;
  max-width: 240px;
  min-width: 240px;
}

.quantity-cell {
  width: 85px;
  max-width: 85px;
  min-width: 85px;
}

.unit-cell {
  width: 85px;
  max-width: 85px;
  min-width: 85px;

  &--small {
    width: 60px;
    max-width: 60px;
    min-width: 60px;
  }
}

.price-cell {
  $width: 110px;
  width: $width;
  max-width: $width;
  min-width: $width;
  text-align: right;

  &.match-cell {
    width: $width - 1px;
    max-width: $width - 1px;
    min-width: $width - 1px;
  }
}

.items-to-match-table {
  & td {
    border: none;
  }

  & td:first-child {
    padding-left: 0;
  }

  & td:last-child {
    padding-right: 0;
  }
}

.items-area {
  // Window height minus header height minus footer height minus padding
  height: calc(100vh - 68px - 68px - 2px);
  overflow-y: auto;
}

.empty-state {
  text-align: center;
  padding-top: 30vh;
}

.empty-drag-area {
  position: relative;
  height: 85%;
  border: 2px dashed rgb(var(--v-theme-light-grey));

  & .empty-state {
    padding-top: 60%;
  }
}

.empty-draggable {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
}

.ghost-empty {
  background: $color;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
}
</style>
